import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Edit, Plus, Shield, Trash, User as UserIcon, Search, Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useUsersStore, type User } from "@/lib/stores/users";
import { CompleteDeletionDialog } from "./CompleteDeletionDialog";
import { removeUserCompletely } from "@/lib/utils/completeUserDeletion";
import { getAuthRedirectUrl } from "@/lib/utils/authRedirect";

const userFormSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Invalid email address"),
  password: z.string().optional(),
  confirmPassword: z.string().optional(),
  role: z.enum(["admin", "doctor", "staff"]),
  specialty: z.string().optional(),
  registrationNumber: z.string().optional(),
}).superRefine((data, ctx) => {
  // Only validate password for new users
  if (!data.password && !data.confirmPassword) {
    return true;
  }

  if (data.password) {
    if (data.password.length < 8) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Password must be at least 8 characters",
        path: ["password"],
      });
    }
    if (!/[A-Z]/.test(data.password)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Password must contain at least one uppercase letter",
        path: ["password"],
      });
    }
    if (!/[a-z]/.test(data.password)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Password must contain at least one lowercase letter",
        path: ["password"],
      });
    }
    if (!/[0-9]/.test(data.password)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Password must contain at least one number",
        path: ["password"],
      });
    }
    if (!/[^A-Za-z0-9]/.test(data.password)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Password must contain at least one special character",
        path: ["password"],
      });
    }
  }

  if (data.password !== data.confirmPassword) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Passwords do not match",
      path: ["confirmPassword"],
    });
  }

  // When role is doctor, specialty and registration number are required
  if (data.role === 'doctor') {
    if (!data.specialty || data.specialty.trim().length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Specialty is required for doctors",
        path: ["specialty"],
      });
    }
    if (!data.registrationNumber || data.registrationNumber.trim().length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Registration number is required for doctors",
        path: ["registrationNumber"],
      });
    }
  }
});

type UserFormValues = z.infer<typeof userFormSchema>;

export function UsersManagement() {
  const { toast } = useToast();
  const { users, isLoading, error, fetchUsers, addUser, updateUser, removeUser, removeUserWithCascade } = useUsersStore();
  const [activeTab, setActiveTab] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [isAddUserDialogOpen, setIsAddUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [isCompleteDeletionDialogOpen, setIsCompleteDeletionDialogOpen] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    if (error) {
      toast({
        title: "Error",
        description: error,
        variant: "destructive",
      });
    }
  }, [error, toast]);

  const form = useForm<UserFormValues>({
    resolver: zodResolver(userFormSchema),
    defaultValues: {
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
      role: "staff",
      specialty: "",
      registrationNumber: "",
    },
  });

  const filteredUsers = activeTab === "all" 
    ? users.filter(user => {
        const name = user.name || '';
        const email = user.email || '';
        const query = searchQuery.toLowerCase();
        return name.toLowerCase().includes(query) || email.toLowerCase().includes(query);
      })
    : users.filter(user => {
        const name = user.name || '';
        const email = user.email || '';
        const query = searchQuery.toLowerCase();
        return user.role === activeTab &&
          (name.toLowerCase().includes(query) || email.toLowerCase().includes(query));
      });

  const handleOpenAddDialog = () => {
    setEditingUser(null);
    form.reset({
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
      role: "staff",
      specialty: "",
      registrationNumber: "",
    });
    setIsAddUserDialogOpen(true);
  };

  const handleAddUser = async (data: UserFormValues) => {
    try {
      // Starting user creation process
      
      // First check if the current user is an admin
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) {
        throw new Error('You must be logged in to add users');
      }

      // Verifying admin permissions

      // Get the current user's profile to check their role
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', currentUser.id)
        .single();

      if (profileError) {
        console.error('Error fetching user profile:', profileError);
        throw new Error('Failed to verify admin permissions');
      }

      // Admin role verified

      if (profile.role !== 'admin') {
        throw new Error('Only administrators can add new users');
      }

      if (!data.password) {
        throw new Error('Password is required for new users');
      }

      // For doctors, ensure required fields are present
      if (data.role === 'doctor') {
        if (!data.specialty) {
          throw new Error('Specialty is required for doctors');
        }
        if (!data.registrationNumber) {
          throw new Error('Registration number is required for doctors');
        }
      }

      // Check if email already exists
      const { data: existingUser, error: existingUserError } = await supabase
        .from('profiles')
        .select('id')
        .eq('email', data.email)
        .maybeSingle();

      if (existingUserError) {
        console.error('Error checking existing user:', existingUserError);
      }

      if (existingUser) {
        throw new Error('A user with this email already exists');
      }

      // Creating authentication user
      const redirectUrl = getAuthRedirectUrl('/confirm-email');
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            role: data.role,
            name: data.name,
            specialty: data.specialty || null,
            registration_number: data.registrationNumber || null
          },
          emailRedirectTo: redirectUrl
        }
      });

      if (authError) {
        console.error('Auth error:', authError);
        throw new Error(`Failed to create user: ${authError.message}`);
      }
      
      if (!authData.user) {
        throw new Error('No user data returned from signup');
      }

      console.log('Auth user created successfully:', authData.user.id);

      // Wait for the trigger to create the profile
      console.log('Waiting for profile trigger...');
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Verify the profile was created
      const { data: newProfile, error: profileCheckError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', authData.user.id)
        .maybeSingle();

      if (profileCheckError) {
        console.error('Profile check error:', profileCheckError);
      }

      console.log('Profile check result:', newProfile);

      if (!newProfile) {
        console.log('Profile not found, attempting manual creation');
        // Try to create the profile manually if the trigger failed
        const { error: manualProfileError } = await supabase
          .from('profiles')
          .insert([{
            id: authData.user.id,
            email: data.email,
            role: data.role,
            name: data.name,
            status: data.role === 'doctor' ? 'active' : 'inactive',
            specialty: data.specialty || null,
            registration_number: data.registrationNumber || null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }]);

        if (manualProfileError) {
          console.error('Manual profile creation error:', manualProfileError);
          throw new Error('Failed to create user profile');
        }
        
        console.log('Manual profile creation successful');
      }

      // If the user is a doctor, create an entry in the doctors table
      if (data.role === 'doctor') {
        try {
          const doctorData = {
            id: authData.user.id,
            name: data.name,
            specialization: data.specialty,
            registration_number: data.registrationNumber, // Fixed: use snake_case
            status: 'active',
            patients_today: 0, // Fixed: use snake_case
            contact_number: data.email, // Use email as contact number
            active: true, // Add the active field
            available: false, // Add the available field
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };

          console.log('Creating doctor profile with data:', doctorData);

          const { error: doctorError } = await supabase
            .from('doctors')
            .insert([doctorData]);

          if (doctorError) {
            console.error('Doctor creation error:', doctorError);
            // Try to update the profile status to indicate the issue
            await supabase
              .from('profiles')
              .update({ status: 'inactive' })
              .eq('id', authData.user.id);
              
            throw new Error(`Failed to create doctor profile: ${doctorError.message}`);
          }

          console.log('Doctor profile created successfully');
        } catch (doctorError: any) {
          console.error('Error in doctor creation:', doctorError);
          toast({
            title: "Error creating doctor profile",
            description: doctorError.message,
            variant: "destructive",
          });
          // Don't throw here, as the user is already created
          return;
        }
      }

      console.log('User creation completed successfully');
      setIsAddUserDialogOpen(false);
      form.reset();
      
      // Refresh the users list
      await fetchUsers();
      
      toast({
        title: "User added",
        description: "The user has been successfully added.",
      });
    } catch (error: any) {
      console.error('Error adding user:', error);
      toast({
        title: "Error adding user",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleEditUser = (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      setEditingUser(user);
      form.reset({
        name: user.name || "",
        email: user.email || "",
        password: "",
        confirmPassword: "",
        role: user.role || "staff",
        specialty: user.specialty || "",
        registrationNumber: user.registrationNumber || "",
      });
      setIsAddUserDialogOpen(true);
    }
  };

  const handleUpdateUser = async (data: UserFormValues) => {
    if (!editingUser) return;

    try {
      // First check if the current user is an admin
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) {
        throw new Error('You must be logged in to update users');
      }

      // Get the current user's profile to check their role
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', currentUser.id)
        .single();

      if (profileError) {
        throw new Error('Failed to verify admin permissions');
      }

      if (profile.role !== 'admin') {
        throw new Error('Only administrators can update users');
      }

      // Update the user profile in Supabase
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          name: data.name,
          email: data.email,
          role: data.role,
          status: editingUser.status,
          specialty: data.specialty || null,
          registration_number: data.registrationNumber || null
        })
        .eq('id', editingUser.id);

      if (updateError) throw updateError;

      // If the user is a doctor, update the doctors table
      if (data.role === 'doctor') {
        const { error: doctorError } = await supabase
          .from('doctors')
          .update({
            name: data.name,
            specialization: data.specialty || '',
            registration_number: data.registrationNumber || '', // Fixed: use snake_case
          })
          .eq('id', editingUser.id);

        if (doctorError) throw doctorError;
      }

      // Update local state
      const updatedUser: User = {
        ...editingUser,
        name: data.name,
        email: data.email,
        role: data.role,
        status: editingUser.status,
        specialty: data.specialty,
        registrationNumber: data.registrationNumber,
      };

      await updateUser(updatedUser);
      setIsAddUserDialogOpen(false);
      setEditingUser(null);
      form.reset();

      toast({
        title: "User updated",
        description: "The user has been successfully updated.",
      });
    } catch (error: any) {
      toast({
        title: "Error updating user",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleDeleteUser = (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      setUserToDelete(user);
      setIsCompleteDeletionDialogOpen(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    
    try {
      await removeUserWithCascade(userToDelete.id);
      toast({
        title: "User deactivated",
        description: `The user ${userToDelete.name} has been successfully deactivated and can no longer login.`,
      });
    } catch (error: any) {
      toast({
        title: "Error deleting user",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleConfirmDeactivate = async () => {
    if (!userToDelete) return;
    
    try {
      await removeUserWithCascade(userToDelete.id);

      await fetchUsers();
      setIsCompleteDeletionDialogOpen(false);
      setUserToDelete(null);

      toast({
        title: "User deactivated",
        description: `The user ${userToDelete.name} has been successfully deactivated.`,
      });
    } catch (error: any) {
      toast({
        title: "Error deactivating user",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleConfirmComplete = async () => {
    if (!userToDelete) return;

    const userIdToDelete = userToDelete.id;
    const userNameToDelete = userToDelete.name;

    try {
      const result = await removeUserCompletely(userIdToDelete);
      if (result.success) {
        await new Promise(resolve => setTimeout(resolve, 200));
        await removeUser(userIdToDelete);
        await fetchUsers();

        toast({
          title: "User completely deleted",
          description: `The user ${userNameToDelete} has been completely removed from all tables.`,
        });
        setIsCompleteDeletionDialogOpen(false);
        setUserToDelete(null);
      } else {
        throw new Error(result.error || 'Failed to delete user');
      }
    } catch (error: any) {
      toast({
        title: "Error deleting user",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case "admin":
        return <Shield className="h-4 w-4" />;
      case "doctor":
        return <UserIcon className="h-4 w-4" />;
      default:
        return <UserIcon className="h-4 w-4" />;
    }
  };

  const getInitials = (name: string | null | undefined) => {
    if (!name || name.trim() === "") {
      return "?";
    }
    return name
      .split(" ")
      .map((n) => n[0])
      .filter(Boolean)
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Users Management</CardTitle>
          <CardDescription>
            Manage your clinic's users and their roles
          </CardDescription>
        </div>
        <Button onClick={handleOpenAddDialog}>
          <Plus className="mr-2 h-4 w-4" />
          Add User
        </Button>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between mb-4">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-4 w-full">
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="admin">Admins</TabsTrigger>
              <TabsTrigger value="doctor">Doctors</TabsTrigger>
              <TabsTrigger value="staff">Staff</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center p-4">
            Loading users...
          </div>
        ) : filteredUsers.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Email Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Avatar>
                        <AvatarImage src={user.avatarUrl} />
                        <AvatarFallback>{getInitials(user.name)}</AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="font-medium">{user.name || "No Name"}</div>
                        <div className="text-sm text-muted-foreground">{user.email || "No Email"}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {getRoleIcon(user.role)}
                      <span className="capitalize">{user.role}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={user.status === "active" ? "default" : "destructive"}
                      className={
                        user.status === "active"
                          ? "bg-green-100 text-green-800 hover:bg-green-100"
                          : "bg-red-100 text-red-800 hover:bg-red-100"
                      }
                    >
                      {user.status === "active" ? "✅ Verified" : "❌ Unverified"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEditUser(user.id)}>
                          <Edit className="mr-2 h-4 w-4" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDeleteUser(user.id)}>
                          <Trash className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="text-center py-4 text-muted-foreground">
            No users found
          </div>
        )}

        <Dialog open={isAddUserDialogOpen} onOpenChange={setIsAddUserDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingUser ? "Edit User" : "Add New User"}</DialogTitle>
              <DialogDescription>
                {editingUser
                  ? "Update the user's information below."
                  : "Fill in the user's information below to create a new account."}
              </DialogDescription>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(editingUser ? handleUpdateUser : handleAddUser)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input {...field} type="email" autoComplete="email" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {!editingUser && (
                  <>
                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Password</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Input
                                {...field}
                                type={showPassword ? "text" : "password"}
                                autoComplete="new-password"
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                onClick={() => setShowPassword(!showPassword)}
                              >
                                {showPassword ? (
                                  <EyeOff className="h-4 w-4" />
                                ) : (
                                  <Eye className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="confirmPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Confirm Password</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Input
                                {...field}
                                type={showConfirmPassword ? "text" : "password"}
                                autoComplete="new-password"
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              >
                                {showConfirmPassword ? (
                                  <EyeOff className="h-4 w-4" />
                                ) : (
                                  <Eye className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </>
                )}
                <FormField
                  control={form.control}
                  name="role"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Role</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a role" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="doctor">Doctor</SelectItem>
                          <SelectItem value="staff">Staff</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {form.watch("role") === "doctor" && (
                  <>
                    <FormField
                      control={form.control}
                      name="specialty"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Specialty</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="registrationNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Registration Number</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </>
                )}
                <DialogFooter>
                  <Button type="submit">
                    {editingUser ? "Update User" : "Add User"}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>

        <CompleteDeletionDialog
          isOpen={isCompleteDeletionDialogOpen}
          onClose={() => {
            setIsCompleteDeletionDialogOpen(false);
            setUserToDelete(null);
          }}
          onConfirmDeactivate={handleConfirmDeactivate}
          onConfirmComplete={handleConfirmComplete}
          userToDelete={userToDelete}
        />
      </CardContent>
    </Card>
  );
}
